// src/whiteboard.js
// ═══════════════════════════════════════════════════════════════
// WHITEBOARD — Reece's misc notes (10/2/26)
// ═══════════════════════════════════════════════════════════════
// Reece, 10/2: "a bubble on the opposite side of the chatbot, to be able to
// essentially save text as if it's on a white board … Not so much as an
// automated to do list, but me just being able to track misc notes, and then
// see them on that main screen".
//
// ONE store per staff login (the pattern of src/libraryChat.js — module-level
// store + subscribe + a React hook with a selector), shared by the two windows
// onto it: the bottom-left bubble (src/views/WhiteboardDock.jsx) and the
// Command Center panel (src/views/Whiteboard.jsx, #cc-whiteboard). A note
// added in one is in the other at once. NOT a to-do list: no checkboxes, due
// dates or reminders, and nothing here feeds the Follow-ups bell or a count.
//
// Note: {id, body, pinned, createdAt, updatedAt, erasedAt|null} (ISO times)
//   id        "WB-<time base36>-<random base36>" — never length-based (the
//             length-based MK/LN ids were a real bug in this app)
//   body      free text, at most WB_MAX characters — a longer one is REFUSED
//             with a message, never cut
//   erasedAt  soft delete: Erase sets it, Restore clears it. Nothing in the
//             app ever deletes a row.
//
// Persistence is PER ROW — never the whole board (an autosave that rewrote a
// whole table is how this app lost loans and marketing leads before):
//   • Supabase table whiteboard_notes (supabase/2026-10-02-whiteboard.sql):
//     ONE upsert of ONE row per add / edit / pin / erase / restore. Typing in
//     a note is held as an open EDIT (state.edits) and becomes the note
//     WB_DEBOUNCE_MS after the last keystroke (then that one row goes out) —
//     and at once on blur, on close / unmount, at sign-out (App's flushers)
//     and on pagehide / beforeunload / tab hidden. Esc before that point
//     leaves the note exactly as it was (no new date, no write).
//   • localStorage "loanflow_whiteboard_<staff>" mirrors the board plus the
//     ids the server hasn't confirmed yet ("dirty"). It is the only copy while
//     the table is missing (42P01 / PGRST205 → latched for the session, like
//     scenarioStore) or Supabase isn't configured. A note stays dirty until
//     the server confirms its row; the next session that finds the table
//     uploads every dirty note, one row at a time. (LS_KEY loanflow_v1 is
//     never touched.) Two tabs share that copy, so it is never written whole
//     from one tab's memory: every write first takes in what another tab
//     wrote (absorb — note by note, newest wins, its unconfirmed marks kept),
//     and a "storage" event takes the other tab's notes in at once.
//   • "loanflow_whiteboard_<staff>:drafts" keeps the half-typed text — the add
//     box and any open edit (an edit over WB_MAX included) — one field at a
//     time, so a reload, the idle sign-out or closing the tab never loses it.
//   • refresh() — at sign-in, when the window regains focus / becomes
//     visible, when the bubble opens and when the Command Center shows the
//     panel — reads the owner's rows and merges note by note: the newest
//     updatedAt wins, so another device's edit shows up and an edit still in
//     flight here isn't overwritten by an older server copy. Then it re-sends
//     whatever is still dirty (the notes marked "Not saved — will retry").
//   • A failed write keeps the note in memory + localStorage and marks it
//     (status[id] = "failed"); it is never shown as saved until it is.

import { useEffect, useSyncExternalStore } from 'react';
import { supabase, isSupabaseReady } from './supabase';

export const WB_TABLE = "whiteboard_notes";
export const WB_MAX = 5000;                       // characters per note
export const WB_LS_PREFIX = "loanflow_whiteboard_";
export const WB_DEBOUNCE_MS = 800;                 // typing → one row write
export const WB_SQL_FILE = "supabase/2026-10-02-whiteboard.sql";
const PAGE = 1000;                                 // PostgREST's default row cap per request
const MAX_PAGES = 50;
const REFRESH_GAP_MS = 4000;                       // focus + open + panel can all ask at once
const COLS = "id,owner,body,pinned,created_at,updated_at,erased_at";

export function wbKey(staff) { return WB_LS_PREFIX + (staff || "reece"); }

// ═══════ pure helpers ═══════
function rand36(n) {
  let s = "";
  try {
    const c = typeof window !== "undefined" ? window.crypto : null;
    if (c && typeof c.getRandomValues === "function") {
      const a = new Uint32Array(n);
      c.getRandomValues(a);
      for (let i = 0; i < n; i++) s += (a[i] % 36).toString(36);
      return s;
    }
  } catch (e) { /* fall back below */ }
  while (s.length < n) s += Math.random().toString(36).slice(2);
  return s.slice(0, n);
}
// A fresh id, never one `taken(id)` says is in use.
export function newNoteId(taken) {
  for (let i = 0; i < 20; i++) {
    const id = "WB-" + Date.now().toString(36) + "-" + rand36(8);
    if (typeof taken !== "function" || !taken(id)) return id;
  }
  return "WB-" + Date.now().toString(36) + "-" + rand36(16);
}

const msOf = function (v) { const t = Date.parse(v); return Number.isFinite(t) ? t : 0; };
const isoOf = function (v, fb) { const t = Date.parse(v); return Number.isFinite(t) ? new Date(t).toISOString() : fb; };
// A local change is always newer than the version it changed — even when the
// other device's clock ran ahead and stamped that version "in the future".
function stampAfter(prevIso) { return new Date(Math.max(Date.now(), msOf(prevIso) + 1)).toISOString(); }

export function normNote(n) {
  if (!n || typeof n !== "object" || typeof n.id !== "string" || !n.id) return null;
  const now = new Date().toISOString();
  const createdAt = isoOf(n.createdAt, now);
  return {
    id: n.id,
    body: typeof n.body === "string" ? n.body : String(n.body == null ? "" : n.body),
    pinned: n.pinned === true,
    createdAt: createdAt,
    updatedAt: isoOf(n.updatedAt, createdAt),
    erasedAt: n.erasedAt ? isoOf(n.erasedAt, null) : null,
  };
}
export function rowToNote(r) {
  return r ? normNote({ id: r.id, body: r.body, pinned: r.pinned, createdAt: r.created_at, updatedAt: r.updated_at, erasedAt: r.erased_at }) : null;
}
// owner is sent explicitly: the table's RLS checks owner = auth_user_key().
export function noteToRow(n, owner) {
  return { id: n.id, owner: owner, body: n.body, pinned: !!n.pinned, created_at: n.createdAt, updated_at: n.updatedAt, erased_at: n.erasedAt || null };
}
const byId = function (a, b) { return a.id < b.id ? 1 : a.id > b.id ? -1 : 0; };
// The board: pinned first, then newest first (by when it was ADDED, so a note
// doesn't jump to the top while it is being edited).
export function sortActive(notes) {
  return (notes || []).filter(function (n) { return !n.erasedAt; }).sort(function (a, b) {
    return (Number(b.pinned) - Number(a.pinned)) || (msOf(b.createdAt) - msOf(a.createdAt)) || byId(a, b);
  });
}
export function sortErased(notes) {
  return (notes || []).filter(function (n) { return !!n.erasedAt; }).sort(function (a, b) {
    return (msOf(b.erasedAt) - msOf(a.erasedAt)) || byId(a, b);
  });
}

// Merge a COMPLETE server read into the local board, note by note.
//   keepLocal(id) — the note has a change the server hasn't confirmed (dirty)
//                   or was changed / confirmed while the read was in flight.
// → {notes, push: ids to (re)send, confirmed: ids the server now matches}
//   both sides: the newer updatedAt wins (a tie is the same version → server)
//   local only: kept + pushed when keepLocal, else dropped (a clean note the
//               server no longer has is not this device's to bring back)
//   server only: taken (added on another device)
export function mergeNotes(local, server, keepLocal) {
  const S = new Map();
  (server || []).forEach(function (n) { if (n && n.id) S.set(n.id, n); });
  const keep = typeof keepLocal === "function" ? keepLocal : function () { return false; };
  const out = [], push = [], confirmed = [];
  const seen = new Set();
  (local || []).forEach(function (l) {
    if (!l || !l.id || seen.has(l.id)) return;
    seen.add(l.id);
    const s = S.get(l.id);
    if (!s) { if (keep(l.id)) { out.push(l); push.push(l.id); } return; }
    if (msOf(l.updatedAt) > msOf(s.updatedAt)) { out.push(l); push.push(l.id); }
    else { out.push(s); confirmed.push(l.id); }
  });
  S.forEach(function (s, id) { if (!seen.has(id)) out.push(s); });
  return { notes: out, push: push, confirmed: confirmed };
}

// ── errors ──
// A column the request named isn't there (42703 / PGRST204) is NOT a missing
// table — checked first so its "could not find" wording can't trip the latch.
function columnMissing(err) {
  if (!err) return false;
  return err.code === "42703" || err.code === "PGRST204"
    || /column .* does not exist|could not find the '[^']*' column/i.test(err.message || "");
}
// Only "the table isn't there" latches. PostgREST's PGRST002 503 ("Could not
// query the database for the schema cache. Retrying.") shows up for a moment
// while it reloads its schema (right after any migration, a restart) — that
// is a retryable load error, never a reason to stop using the table for the
// whole session.
export function tableMissing(err) {
  if (!err || columnMissing(err) || err.code === "PGRST002") return false;
  return err.code === "42P01" || err.code === "PGRST205"
    || /could not find the table|relation "?[\w.]*whiteboard_notes"? does not exist/i.test(err.message || "");
}
const errText = function (e) { return (e && (e.message || e.error_description)) || String(e || "unknown error"); };

// ── localStorage (try/catch everywhere: private mode, full, blocked) ──
function lsLoad(who) {
  try {
    const raw = window.localStorage.getItem(wbKey(who));
    if (!raw) return { notes: [], dirty: [] };
    const o = JSON.parse(raw);
    const seen = new Map();
    (o && Array.isArray(o.notes) ? o.notes : []).map(normNote).filter(Boolean).forEach(function (n) {
      const had = seen.get(n.id);
      if (!had || msOf(n.updatedAt) > msOf(had.updatedAt)) seen.set(n.id, n);
    });
    const notes = Array.from(seen.values());
    const dirty = (o && Array.isArray(o.dirty) ? o.dirty : []).filter(function (id) { return typeof id === "string" && seen.has(id); });
    return { notes: notes, dirty: dirty };
  } catch (e) { return { notes: [], dirty: [] }; }
}
function lsSave(who, notes, dirty) {
  try { window.localStorage.setItem(wbKey(who), JSON.stringify({ v: 1, notes: notes, dirty: dirty })); return true; }
  catch (e) { return false; }
}
// The half-typed text: {add: the add box, edits: {id: {text, orig, committed}}}.
// Written one field at a time (read → change that field → write), so one
// tab's typing never wipes another tab's. Every other typed-but-unsaved box
// in the app survives sign-out via App's flushers; this one survives it here.
export function wbDraftKey(staff) { return WB_LS_PREFIX + (staff || "reece") + ":drafts"; }
function draftsLoad(who) {
  try {
    const raw = window.localStorage.getItem(wbDraftKey(who));
    const o = raw ? JSON.parse(raw) : null;
    const src = o && o.edits && typeof o.edits === "object" ? o.edits : {};
    const edits = {};
    Object.keys(src).forEach(function (id) {
      const e = src[id];
      if (e && typeof e.text === "string" && typeof e.orig === "string") edits[id] = { text: e.text, orig: e.orig, committed: e.committed === true };
    });
    return { add: o && typeof o.add === "string" ? o.add : "", edits: edits };
  } catch (e) { return { add: "", edits: {} }; }
}
function draftsPatch(who, fn) {
  try {
    const d = draftsLoad(who);
    fn(d);
    const k = wbDraftKey(who);
    if (!d.add && !Object.keys(d.edits).length) window.localStorage.removeItem(k);
    else window.localStorage.setItem(k, JSON.stringify({ v: 1, add: d.add, edits: d.edits }));
    return true;
  } catch (e) { return false; }
}

// ═══════ session state (reset at sign-out) ═══════
// "unknown" → not probed this session; "present"; "missing" → the SQL hasn't
// been run: stop calling the table until the next session.
let tableState = "unknown";
let era = 0;              // bumped by resetWhiteboard: a late reply from the old session lands nowhere
let warned = false;
export function whiteboardTableState() { return tableState; }

function warnMissing(err) {
  if (warned) return;
  warned = true;
  try {
    console.warn("LoanFlow: the whiteboard_notes table isn't there yet — notes are kept on this device until " + WB_SQL_FILE + " is run.", err && (err.message || err));
  } catch (e) { /* ignore */ }
}

// One read of every row this login owns (paged — PostgREST caps a request).
function fetchAll(who) {
  const out = [];
  const page = function (n) {
    const from = n * PAGE;
    return Promise.resolve()
      .then(function () {
        return supabase.from(WB_TABLE).select(COLS).eq("owner", who)
          .order("created_at", { ascending: true }).order("id", { ascending: true })
          .range(from, from + PAGE - 1);
      })
      .then(function (r) {
        if (!r || r.error) throw (r && r.error) || new Error("No reply from the database.");
        const rows = Array.isArray(r.data) ? r.data : [];
        rows.forEach(function (x) { out.push(x); });
        if (rows.length < PAGE || n + 1 >= MAX_PAGES) return out;
        return page(n + 1);
      });
  };
  return page(0);
}

function localModeOf() {
  if (!isSupabaseReady()) return { mode: "local", localReason: "offline" };
  if (tableState === "missing") return { mode: "local", localReason: "no_table" };
  return { mode: tableState === "present" ? "server" : "unknown", localReason: null };
}

// ═══════ the store ═══════
// Snapshot (getSnapshot):
//   notes        every note, erased ones included
//   active       the board (sortActive) · erased — "Recently erased" (sortErased)
//   status       {id: "failed"} — "Not saved — will retry"
//   draft        the half-typed new note (shared: start it in the bubble,
//                finish it on the Command Center; kept on this device)
//   edits        {id: {text, orig, committed, queued}} — notes open for
//                editing: text = what is in the box (it may be blank or over
//                WB_MAX — then it is NOT the note, and is kept until it's
//                fixed or Esc'd), orig = the note before this edit (what Esc /
//                an emptied box puts back), committed = some of this edit is
//                already the note (so Esc has to write orig back), queued =
//                it becomes the note when the typing pause runs out
//   mode         "unknown" | "server" | "local"; localReason "offline" | "no_table"
//   loadError    the last refresh failed (showing this device's copy)
//   lsError      this device's copy couldn't be written
//   loaded       a refresh has settled at least once
function createStore(staff) {
  const who = staff || "reece";
  const boot = lsLoad(who);
  const bootDrafts = draftsLoad(who);
  const dirty = new Set(boot.dirty);
  const subs = new Set();
  const timers = new Map();     // id → debounce timer (update())
  const commitTimers = new Map(); // id → typing-pause timer of an open edit
  const inflight = new Map();   // id → the write in flight
  const again = new Set();      // changed while its write was in flight → send once more
  const changed = new Map();    // id → tick of its last local change
  const confirmedAt = new Map(); // id → tick of the server's last confirm of it
  // id → (ms) the version of each note this tab already has had: written to
  // this device's copy, read, or taken in from another tab. A version in the
  // device's copy that this tab already had is its own past — never taken
  // back in (that is how a note a refresh dropped stays dropped).
  const known = new Map();
  boot.notes.forEach(function (n) { known.set(n.id, msOf(n.updatedAt)); });
  let tick = 0;
  let disposed = false;
  let refreshing = null;
  let lastRefresh = 0;
  const m0 = localModeOf();
  // An edit left open by a sign-out, a reload or a crash comes back open —
  // but only when it holds text the note doesn't have.
  const bootEdits = {};
  Object.keys(bootDrafts.edits).forEach(function (id) {
    const n = boot.notes.find(function (x) { return x.id === id; });
    const e = bootDrafts.edits[id];
    if (n && e.text !== n.body) bootEdits[id] = { text: e.text, orig: e.orig, committed: e.committed, queued: false };
  });
  let state = {
    staff: who, notes: boot.notes, active: sortActive(boot.notes), erased: sortErased(boot.notes),
    status: {}, draft: bootDrafts.add, edits: bootEdits, mode: m0.mode, localReason: m0.localReason, loadError: null, lsError: false,
    loaded: m0.mode === "local",
  };

  const emit = function () { subs.forEach(function (f) { try { f(); } catch (e) { /* a view went away */ } }); };
  function set(patch) {
    if (disposed) return;
    const next = Object.assign({}, state, patch);
    if (patch.notes) { next.active = sortActive(next.notes); next.erased = sortErased(next.notes); }
    state = next;
    emit();
  }
  function setStatus(id, v) {
    const cur = state.status[id] || null;
    if (cur === (v || null)) return;
    const st = Object.assign({}, state.status);
    if (v) st[id] = v; else delete st[id];
    set({ status: st });
  }
  // Take in what ANOTHER tab of this browser wrote to this device's copy —
  // its new notes, its newer versions and the notes it still has to upload —
  // so this tab's next write can't drop them. (Each tab used to write the
  // whole board from its own memory: tab B, open since before, wiped the
  // note tab A had just added — for good while the table is missing.)
  // Note by note:
  //   same version here        → its "unconfirmed" mark clears if the other
  //                              tab saw the server confirm it
  //   a version this tab had   → its own past (or a note a refresh dropped):
  //                              left out
  //   newer, or not here       → taken, with the other tab's mark
  //   older                    → this tab's newer one stays
  function absorb(stored) {
    if (disposed || !stored) return false;
    const sDirty = new Set(stored.dirty);
    const at = new Map();
    state.notes.forEach(function (n, i) { at.set(n.id, i); });
    let list = null, st = null;
    const clean = function (id) {
      dirty.delete(id);
      if (timers.has(id)) { clearTimeout(timers.get(id)); timers.delete(id); }
      if ((st || state.status)[id]) { if (!st) st = Object.assign({}, state.status); delete st[id]; }
    };
    stored.notes.forEach(function (s) {
      const i = at.has(s.id) ? at.get(s.id) : -1;
      const mine = i >= 0 ? (list || state.notes)[i] : null;
      const sMs = msOf(s.updatedAt);
      if (mine && msOf(mine.updatedAt) === sMs) {
        if (!sDirty.has(s.id) && dirty.has(s.id) && !inflight.has(s.id)) clean(s.id);
        return;
      }
      if (known.get(s.id) === sMs) return;
      if (mine && msOf(mine.updatedAt) > sMs) return;
      if (!list) list = state.notes.slice();
      if (i >= 0) list[i] = s; else { at.set(s.id, list.length); list.push(s); }
      known.set(s.id, sMs);
      changed.set(s.id, ++tick);            // a write of the older one in flight → sends this one after
      if (sDirty.has(s.id)) dirty.add(s.id); else clean(s.id);
    });
    if (!list && !st) return false;
    const patch = {};
    if (list) patch.notes = list;
    if (st) patch.status = st;
    set(patch);
    return true;
  }
  // This device's copy of the board (the table is never rewritten whole —
  // only localStorage is, and only after taking in what another tab wrote
  // there). In local mode it IS the save, so a failure marks the note it
  // was saving.
  function persist(id) {
    if (disposed) return;
    absorb(lsLoad(who));
    const ok = lsSave(who, state.notes, Array.from(dirty));
    if (ok) state.notes.forEach(function (n) { known.set(n.id, msOf(n.updatedAt)); });
    if (ok === state.lsError) set({ lsError: !ok });
    if (id && state.mode === "local") setStatus(id, ok ? null : "failed");
  }
  // Another tab wrote this device's copy (its "storage" event) — take it in.
  function syncTabs() { return absorb(lsLoad(who)); }
  const canUseTable = function () { return isSupabaseReady() && tableState !== "missing"; };
  function latchMissing(err) {
    tableState = "missing";
    warnMissing(err);
    timers.forEach(function (t) { clearTimeout(t); });
    timers.clear();
    // Saved on this device — not a failure. Every dirty note waits for a
    // session that finds the table.
    set({ mode: "local", localReason: "no_table", status: {}, loaded: true });
    persist();
  }

  // Send ONE note's row (debounce cleared). Resolves true once the server
  // confirms it, false otherwise (local only, failed, signed out).
  function send(id) {
    if (disposed) return Promise.resolve(false);
    if (timers.has(id)) { clearTimeout(timers.get(id)); timers.delete(id); }
    if (inflight.has(id)) { again.add(id); return inflight.get(id); }
    if (!dirty.has(id)) return Promise.resolve(true);
    if (!canUseTable()) return Promise.resolve(false);       // local only — this device's copy is the save
    const note = state.notes.find(function (n) { return n.id === id; });
    if (!note) { dirty.delete(id); return Promise.resolve(false); }
    const sentTick = changed.get(id) || 0;
    const myEra = era;
    const p = Promise.resolve()
      .then(function () { return supabase.from(WB_TABLE).upsert(noteToRow(note, who), { onConflict: "id" }).select("id"); })
      .then(function (r) {
        if (!r || r.error) throw (r && r.error) || new Error("No reply from the database.");
        // Only a returned row is a confirmed save.
        if (!Array.isArray(r.data) || !r.data.some(function (x) { return x && x.id === id; })) throw new Error("The database didn't confirm this note.");
        if (disposed || myEra !== era) return true;
        tableState = "present";
        // Unchanged since it was sent → on the server. Changed meanwhile → send
        // the newer text once more (unless a debounce is already waiting to).
        if ((changed.get(id) || 0) === sentTick) dirty.delete(id);
        else if (!timers.has(id)) again.add(id);
        confirmedAt.set(id, ++tick);                                  // a read already in flight is older than this
        const patch = {};
        if (state.mode !== "server") { patch.mode = "server"; patch.localReason = null; }
        if (state.status[id]) { patch.status = Object.assign({}, state.status); delete patch.status[id]; }
        if (Object.keys(patch).length) set(patch);
        persist();
        return true;
      })
      .catch(function (err) {
        if (disposed || myEra !== era) return false;
        if (tableMissing(err)) { latchMissing(err); return false; }
        setStatus(id, "failed");
        return false;
      })
      .then(function (ok) {
        if (inflight.get(id) === p) inflight.delete(id);
        if (!disposed && myEra === era && again.has(id)) { again.delete(id); if (dirty.has(id)) return send(id); }
        return ok;
      });
    inflight.set(id, p);
    return p;
  }
  function schedule(id) {
    if (timers.has(id)) clearTimeout(timers.get(id));
    timers.set(id, setTimeout(function () { timers.delete(id); send(id); }, WB_DEBOUNCE_MS));
  }

  // One local change to one note: new updatedAt, dirty, this device's copy,
  // then that ONE row out (debounced while typing).
  function change(id, fn, debounce) {
    if (disposed) return false;
    const i = state.notes.findIndex(function (n) { return n.id === id; });
    if (i < 0) return false;
    const prev = state.notes[i];
    const at = stampAfter(prev.updatedAt);
    const next = fn(prev, at);
    if (!next) return false;
    const list = state.notes.slice();
    list[i] = Object.assign({}, next, { updatedAt: at });
    dirty.add(id);
    changed.set(id, ++tick);
    set({ notes: list });
    persist(id);
    if (debounce) schedule(id); else send(id);
    return true;
  }

  // ── actions ──
  // add(text) → {ok:true, id} | {ok:false, error:"empty"|"too_long"|"signed_out"}
  function add(text) {
    const body = String(text == null ? "" : text).trim();
    if (!body) return { ok: false, error: "empty" };
    if (body.length > WB_MAX) return { ok: false, error: "too_long" };
    if (disposed) return { ok: false, error: "signed_out" };
    const now = new Date().toISOString();
    const id = newNoteId(function (x) { return state.notes.some(function (n) { return n.id === x; }); });
    const note = { id: id, body: body, pinned: false, createdAt: now, updatedAt: now, erasedAt: null };
    dirty.add(id);
    changed.set(id, ++tick);
    set({ notes: state.notes.concat([note]) });
    persist(id);
    send(id);
    return { ok: true, id: id };
  }
  // A new body for a note (the row goes out WB_DEBOUNCE_MS later, or on
  // flush). Blank or over WB_MAX is refused.
  function update(id, text) {
    const body = String(text == null ? "" : text);
    if (!body.trim() || body.length > WB_MAX) return false;
    return change(id, function (n) { return n.erasedAt || n.body === body ? null : Object.assign({}, n, { body: body }); }, true);
  }
  function togglePin(id) {
    return change(id, function (n) { return n.erasedAt ? null : Object.assign({}, n, { pinned: !n.pinned }); });
  }
  // Text typed into the note a moment ago is part of what he erases (Restore
  // brings it back with it); an edit too long to save stays open for Restore.
  function erase(id) {
    const e0 = state.edits[id];
    if (e0 && fits(e0.text)) commitEdit(id);
    const ok = change(id, function (n, at) { return n.erasedAt ? null : Object.assign({}, n, { erasedAt: at }); });
    const e = state.edits[id];
    if (ok && e && e.text.length <= WB_MAX) putEdit(id, null);
    return ok;
  }
  function restore(id) {
    return change(id, function (n) { return n.erasedAt ? Object.assign({}, n, { erasedAt: null }) : null; });
  }
  function setDraft(v) {
    const s = String(v == null ? "" : v);
    if (s === state.draft || disposed) return;
    set({ draft: s });
    draftsPatch(who, function (d) { d.add = s; });
  }

  // ── editing a note ──
  // The box's text is an open edit, not the note: it becomes the note
  // WB_DEBOUNCE_MS after the last keystroke (then that ONE row goes out), or
  // at once on finish / flush. Blank or over WB_MAX it never does — it waits,
  // kept here and on this device, until it's fixed or cancelled. So:
  //   • Esc before that point leaves the note exactly as it was — no new
  //     "edited" date, no write;
  //   • a version cut at 5,000 characters is never saved, and text over the
  //     limit is never thrown away (closing the panel, another screen, the
  //     idle sign-out: it comes back open).
  function putEdit(id, e) {
    if (disposed) return;
    const ed = Object.assign({}, state.edits);
    if (e) ed[id] = e; else delete ed[id];
    set({ edits: ed });
    draftsPatch(who, function (d) {
      if (e && (e.committed || e.text !== e.orig)) d.edits[id] = { text: e.text, orig: e.orig, committed: !!e.committed };
      else delete d.edits[id];
    });
  }
  function clearCommit(id) { if (commitTimers.has(id)) { clearTimeout(commitTimers.get(id)); commitTimers.delete(id); } }
  function fits(t) { return !!t.trim() && t.length <= WB_MAX; }
  function startEdit(id) {
    if (disposed) return false;
    if (state.edits[id]) return true;
    const n = state.notes.find(function (x) { return x.id === id; });
    if (!n || n.erasedAt) return false;
    putEdit(id, { text: n.body, orig: n.body, committed: false, queued: false });
    return true;
  }
  function typeEdit(id, text) {
    if (disposed) return false;
    const e = state.edits[id];
    if (!e) return false;
    const t = String(text == null ? "" : text);
    if (t === e.text) return true;
    clearCommit(id);
    const ok = fits(t);
    if (ok) commitTimers.set(id, setTimeout(function () { commitTimers.delete(id); commitEdit(id); }, WB_DEBOUNCE_MS));
    putEdit(id, Object.assign({}, e, { text: t, queued: ok }));
    return true;
  }
  // The edit's text becomes the note now (if it fits and differs) — that
  // row goes out at once. The edit stays open.
  function commitEdit(id) {
    clearCommit(id);
    const e = state.edits[id];
    if (!e) return false;
    const t = e.text;
    if (!fits(t)) { if (e.queued) putEdit(id, Object.assign({}, e, { queued: false })); return false; }
    const did = change(id, function (n) { return n.erasedAt || n.body === t ? null : Object.assign({}, n, { body: t }); }, false);
    const cur = state.edits[id];
    if (cur && (cur.queued || (did && !cur.committed))) putEdit(id, Object.assign({}, cur, { queued: false, committed: cur.committed || did }));
    return did;
  }
  // The note back to how it was before this edit — only if part of the edit
  // already became the note (then it's one more write, newest wins).
  function revertEdit(id, e) {
    if (!e.committed) return false;
    return change(id, function (n) { return n.erasedAt || n.body === e.orig ? null : Object.assign({}, n, { body: e.orig }); }, false);
  }
  // Leaving the box (blur, Done, Ctrl+Enter, the panel closing):
  //   "too_long" → stays open (nothing saved, nothing lost)
  //   "reverted" → it was emptied: the note is back to its pre-edit text
  //   "saved"    → the text is the note
  //   "none"     → no edit was open
  function finishEdit(id) {
    if (disposed) return "none";
    const e = state.edits[id];
    if (!e) return "none";
    if (e.text.length > WB_MAX) { clearCommit(id); if (e.queued) putEdit(id, Object.assign({}, e, { queued: false })); return "too_long"; }
    if (!e.text.trim()) { clearCommit(id); revertEdit(id, e); putEdit(id, null); return "reverted"; }
    commitEdit(id);
    putEdit(id, null);
    return "saved";
  }
  // Esc: drop the edit. Nothing of it reached the note → the note is untouched.
  function cancelEdit(id) {
    if (disposed) return false;
    const e = state.edits[id];
    if (!e) return false;
    clearCommit(id);
    revertEdit(id, e);
    putEdit(id, null);
    return true;
  }

  // Send what is waiting to be sent now (one note, or all) — typing that
  // hasn't paused long enough to become the note becomes it first, and (all)
  // a write that failed earlier gets one more try — then wait for the writes
  // in flight. Never rejects.
  function flush(id) {
    if (disposed) return Promise.resolve(true);
    (id ? [id] : Array.from(commitTimers.keys())).forEach(function (x) { if (commitTimers.has(x)) commitEdit(x); });
    let ids;
    if (id) ids = [id];
    else {
      const all = new Set(Array.from(timers.keys()).concat(Array.from(inflight.keys())));
      if (canUseTable()) Object.keys(state.status).forEach(function (x) { if (dirty.has(x)) all.add(x); });
      ids = Array.from(all);
    }
    return Promise.all(ids.map(function (x) {
      if (timers.has(x) || (state.status[x] === "failed" && !inflight.has(x))) return send(x);
      return inflight.get(x) || Promise.resolve(true);
    })).then(function () { return true; }, function () { return true; });
  }

  // Read the board from the table and merge it in (see mergeNotes), then
  // re-send whatever is still dirty. opts.force skips the short gap that
  // keeps focus + open + the panel from reading three times at once.
  function refresh(opts) {
    const o = opts || {};
    if (disposed) return Promise.resolve(false);
    absorb(lsLoad(who));                 // another tab's notes first (device only: they are the news)
    if (!canUseTable()) {
      const m = localModeOf();
      if (state.mode !== m.mode || state.localReason !== m.localReason || !state.loaded) set({ mode: m.mode, localReason: m.localReason, loaded: true });
      return Promise.resolve(false);
    }
    if (refreshing) return refreshing;
    const hasFailed = Object.keys(state.status).length > 0;
    if (!o.force && state.loaded && !state.loadError && !hasFailed && dirty.size === 0 && Date.now() - lastRefresh < REFRESH_GAP_MS) return Promise.resolve(true);
    const mark = tick;
    const myEra = era;
    const p = fetchAll(who).then(function (rows) {
      if (disposed || myEra !== era) return false;
      tableState = "present";
      lastRefresh = Date.now();
      const server = rows.map(rowToNote).filter(Boolean);
      // A note changed or confirmed since this read started is newer than
      // what the read saw — never dropped by it.
      const since = function (id) { return (changed.get(id) || 0) > mark || (confirmedAt.get(id) || 0) > mark; };
      const m = mergeNotes(state.notes, server, function (id) { return dirty.has(id) || since(id); });
      const st = Object.assign({}, state.status);
      m.confirmed.forEach(function (id) {
        dirty.delete(id); delete st[id];
        if (timers.has(id)) { clearTimeout(timers.get(id)); timers.delete(id); }
      });
      // Re-send what the server lacks — but not a note the server confirmed
      // after this read was taken (the read is just older than that write).
      const push = m.push.filter(function (id) { return dirty.has(id) || !((confirmedAt.get(id) || 0) > mark); });
      push.forEach(function (id) { dirty.add(id); });
      set({ notes: m.notes, status: st, mode: "server", localReason: null, loadError: null, loaded: true });
      persist();
      push.forEach(function (id) { send(id); });
      return true;
    }, function (err) {
      if (disposed || myEra !== era) return false;
      if (tableMissing(err)) { latchMissing(err); return false; }
      set({ loadError: errText(err), loaded: true });
      return false;
    }).then(function (ok) { if (refreshing === p) refreshing = null; return ok; });
    refreshing = p;
    return p;
  }

  // Signed out: nothing more goes out. Open edits and the add box's text
  // stay on this device (the drafts key) for the next login.
  function dispose() {
    disposed = true;
    timers.forEach(function (t) { clearTimeout(t); });
    commitTimers.forEach(function (t) { clearTimeout(t); });
    timers.clear(); commitTimers.clear(); again.clear();
    subs.clear();
  }
  const subscribe = function (fn) { subs.add(fn); return function () { subs.delete(fn); }; };
  const getSnapshot = function () { return state; };
  const pending = function () { return timers.size + commitTimers.size + inflight.size; };

  return { staff: who, subscribe, getSnapshot, add, update, togglePin, erase, restore, setDraft,
    startEdit, typeEdit, finishEdit, cancelEdit, flush, refresh, syncTabs, dispose, pending };
}

const STORES = new Map();
// The store for one staff login, created on first use.
export function getWhiteboardStore(staff) {
  const who = staff || "reece";
  let s = STORES.get(who);
  if (!s) { s = createStore(who); STORES.set(who, s); }
  return s;
}
// Every pending edit out now (App's sign-out flushers, pagehide).
export function flushWhiteboard() {
  return Promise.all(Array.from(STORES.values()).map(function (s) { return s.flush(); })).then(function () { return true; });
}
// Sign-out (and tests): drop the in-memory boards — this device's copy stays
// in localStorage with its unconfirmed marks, for the next login — and
// forget the table probe, so the next session looks for it again.
export function resetWhiteboard() {
  era++;
  STORES.forEach(function (s) { s.dispose(); });
  STORES.clear();
  tableState = "unknown";
  warned = false;
}

const EMPTY = Object.freeze({ staff: null, notes: [], active: [], erased: [], status: {}, draft: "", edits: {}, mode: "unknown", localReason: null, loadError: null, lsError: false, loaded: false });
const noSubscribe = function () { return function () {}; };
// React hook. staff null → an empty, inert snapshot. selector must return a
// field of the snapshot or a primitive (never a new object).
export function useWhiteboard(staff, selector) {
  const store = staff ? getWhiteboardStore(staff) : null;
  const read = store ? store.getSnapshot : function () { return EMPTY; };
  const get = selector ? function () { return selector(read()); } : read;
  return useSyncExternalStore(store ? store.subscribe : noSubscribe, get, get);
}

// App, once per signed-in login that has the whiteboard (null = none): load
// at sign-in, re-read when the window comes back (focus / visible — another
// device may have written), take in another tab's writes to this device's
// copy as they happen ("storage"), and push pending edits out when the tab
// hides or the page is going away.
export function useWhiteboardSync(staff) {
  useEffect(function () {
    if (!staff) return undefined;
    const who = staff;
    const key = wbKey(who);
    const live = function () { return STORES.get(who) || null; };
    getWhiteboardStore(who).refresh({ force: true });
    const onFocus = function () { const s = live(); if (s) s.refresh(); };
    const onVis = function () {
      const s = live(); if (!s) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") s.flush();
      else s.refresh();
    };
    const onLeave = function () { const s = live(); if (s) s.flush(); };
    // Only another tab's write fires this (key null = it cleared storage).
    const onStorage = function (e) {
      if (e && e.key !== null && e.key !== key) return;
      const s = live(); if (s) s.syncTabs();
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("pagehide", onLeave);
    window.addEventListener("beforeunload", onLeave);
    window.addEventListener("storage", onStorage);
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVis);
    return function () {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pagehide", onLeave);
      window.removeEventListener("beforeunload", onLeave);
      window.removeEventListener("storage", onStorage);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVis);
    };
  }, [staff]);
}

// ── relative dates for the "added / edited …" line ──
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export function relTime(iso, now) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const n = Number.isFinite(now) ? now : Date.now();
  const d = Math.max(0, n - t);
  if (d < 60000) return "just now";
  if (d < 3600000) return Math.floor(d / 60000) + "m ago";
  const a = new Date(t), b = new Date(n);
  if (a.toDateString() === b.toDateString()) return Math.floor(d / 3600000) + "h ago";
  const y = new Date(n); y.setDate(y.getDate() - 1);
  if (a.toDateString() === y.toDateString()) return "yesterday";
  if (d < 6 * 86400000) return WEEKDAYS[a.getDay()];
  return (a.getMonth() + 1) + "/" + a.getDate() + (a.getFullYear() !== b.getFullYear() ? "/" + String(a.getFullYear()).slice(2) : "");
}
export function whenLabel(note, now) {
  if (!note) return "";
  const edited = msOf(note.updatedAt) - msOf(note.createdAt) > 1500;
  return (edited ? "edited " : "added ") + relTime(edited ? note.updatedAt : note.createdAt, now);
}
